import React, { useEffect, useState } from 'react';
import type { MessageFormData, MessageModalModel } from '../../type/message';
import { ModalHeader } from '../../components/modalCommon/modalHeader';
import { ModalBody } from '../../components/modalCommon/modalBody';
import { ModalFooter } from '../../components/modalCommon/modalFooter';
import { BaseModal } from '../../components/modalCommon/modalBase';
import { fetchWithAuth } from '../../auth/auth';
import { useToast } from "../../components/toast/toast";

const MessageModal: React.FC<MessageModalModel> = ({
  show,
  onHide,
  iduser,
  email,
  onSuccess
}) => {
  const [formData, setFormData] = useState<MessageFormData>({ text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {showToast} = useToast();

  useEffect(() => {
    if (show) {
      setFormData({ text: '' });
    }
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.text.trim()) return;

    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth('/api/tools/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ iduser, text: formData.text.trim(), email: email }),
      });

      if (res.ok) {
        showToast("Message sent successfully.", "info");
        onHide();
        onSuccess?.();
      } else {
        const data = await res.json();
        showToast(data.error || 'Failed to send message.', "error");
      }
    } catch {
      showToast('Server connection error.', "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal open={show} onClose={onHide} size="md">
      <ModalHeader
        title="Message to Administrator"
        onClose={onHide}
      />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="mb-3">
            <div className="mb-2 text-white-50">We're constantly improving our app. Share your feedback on what we can improve or what you like best.</div>
            <label className="form-label">Message *</label>
            <textarea
              className="form-control bg-dark text-light border-secondary"
              rows={6}
              value={formData.text}
              onChange={(e) =>
                setFormData({ ...formData, text: e.target.value })
              }
              placeholder="Write your message..."
              maxLength={2000}
              required
              disabled={isSubmitting}
            />
            <small className="text-secondary">
              {formData.text.length}/2000
            </small>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !formData.text.trim()}
          >
            {isSubmitting ? 'Sending…' : 'Send'}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default MessageModal;